<template>
    <UiTabbedSearchboxHero
        :blok="blok"
        :airports="airports"
        :terminals="airportsDropdownList.terminals"
        :form-actions="[]"
        :feefo-image-url="feefoImageUrl"
        id="quoteForm"
        @scroll-to-reviews="scrollToReviews"
        @submit-form="submitForm"
        @set-active-tab="setActiveTab"
    />
</template>

<script setup lang="ts">
import { formatDate } from '@nuxt-monorepo/ui/utils/helpers';
const config = useRuntimeConfig();
import { TABS } from '@nuxt-monorepo/ui/components/TabbedSearchbox/constants';
import { useSearchStore } from '@nuxt-monorepo/base/app/store/search';
const searchStore: any = useSearchStore();
const { locale } = useI18n();

const { blok } = defineProps<{ blok: any }>();

const mapTabs = {
    [TABS.AIRPORT_PARKING]: 'Parking',
    [TABS.AIRPORT_LOUNGES]: 'Lounge',
    [TABS.HOTEL_AND_PARKING]: 'HotelWithParking',
};
const activeTab = ref(blok.defaultTab);
const setActiveTab = (tab) => {
    activeTab.value = tab;
    searchStore.updateActiveTab(mapTabs[tab]);
};

const data: any = await useAsyncStoryblok(`sps/global/shared/sps-searchbox-airports`, {
    api: {
        version: config?.public?.STORYBLOK_VERSION === 'published' ? 'published' : 'draft',
        language: locale.value,
    },
    bridge: {},
});

const airportsDropdownList = computed(() => {
    const airports = {};
    data?.data?.value?.data?.story?.content?.Lists.forEach((list) => {
        airports[list.listType] = list.List;
    });

    const terminals =
        data?.data?.value?.data?.story?.content?.Terminals?.map((terminal) => {
            return {
                id: Number(terminal.id),
                name: terminal.name,
                airportId: Number(terminal.airportId),
            };
        }) || [];

    return { airports, terminals };
});

const currentAirports = computed(() => {
    const { airports } = airportsDropdownList.value;
    return airports[activeTab.value] || airports[TABS.AIRPORT_PARKING] || [];
});

const airports = computed(() => ({ airportList: currentAirports.value, withCountries: false }));

function addQueryParamsToUrl(url, params) {
    const urlObj = new URL(url);
    Object.keys(params).forEach((key) => urlObj.searchParams.append(key, params[key]));
    return urlObj.toString();
}

const submitForm = ({ formData, form }) => {
    let query;
    if (activeTab.value === TABS.AIRPORT_PARKING) {
        query = {
            category: 'airportparking',
            airportA: Number(formData.location.id),
            airportA_code: formData.location.code,
            dateA: formatDate(formData.dates.start.date, 'yyyy-mm-dd'),
            dateB: formatDate(formData.dates.end.date, 'yyyy-mm-dd'),
            _dateA: formatDate(formData.dates.start.date, 'day, dd/mm/yy'),
            _dateB: formatDate(formData.dates.end.date, 'day, dd/mm/yy'),
            timeA: formData.dates.start.time,
            timeB: formData.dates.end.time,
            promo: formData.discount,
            passengers: 1,
            agent: 'ONLINE',
            params: [],
            terminal: formData.terminal.id,
            quoteID: '',
            nohotel: 0,
            park_type: 'all',
        };
    } else if (activeTab.value === TABS.AIRPORT_LOUNGES) {
        query = {
            category: 'airportlounge',
            airportA: Number(formData.location.id),
            airportA_code: formData.location.code,
            dateA: formatDate(formData.dates.start.date, 'yyyy-mm-dd'),
            dateB: formatDate(formData.dates.start.date, 'yyyy-mm-dd'),
            _dateA: formatDate(formData.dates.start.date, 'day, dd/mm/yy'),
            _dateB: formatDate(formData.dates.start.date, 'day, dd/mm/yy'),
            timeA: formData.dates.start.time,
            timeB: nextTimeSlot(formData.dates.start.time, 60),
            adults: formData.customers?.adult,
            children: formData.customers?.child,
            passengers: 1,
            promo: '',
            agent: 'Lounges',
            params: [],
            terminal: formData.terminal.id,
            quoteID: '',
        };
    }
    const searchUrl = '/booking-quote/' + query.category + '-results';
    navigateTo(addQueryParamsToUrl(config?.public.BOOKING_URL + searchUrl, query), { external: true });
};

// Feefo
const feefoImageUrl = `https://api.feefo.com/api/logo?merchantidentifier=skypark-secure`;

function scrollToReviews() {
    const header = document.querySelector('header');
    const headerHeight = header?.getBoundingClientRect()?.height || 0;
    const customerReviews = document.querySelector('.customer-reviews');
    const customerReviewsTop = customerReviews?.getBoundingClientRect()?.top + window?.pageYOffset;
    if (customerReviews) {
        window.scrollTo({
            top: customerReviewsTop - headerHeight,
            behavior: 'smooth',
        });
    }
}
</script>

<style lang="postcss" scoped>
:deep(.container) {
    @apply mx-auto;
}
.tabbed-searchbox-hero--hero :deep(.container) {
    @apply mx-auto md:px-8;
}

.tabbed-searchbox-hero--hero:has(+ #hero_footer.with-float-image) {
    @apply lg:pb-[250px];
}

:deep(.num-selector__button) {
    @apply text-[#055988];
}

:deep(.button-primary),
:deep(.search-submit),
:deep(.travel-insurance__link) {
    @apply bg-[#055988] text-white;
    &:focus,
    &:hover {
        box-shadow: inset 0 0 2px 100px rgba(0, 0, 0, 0.16);
    }
}

:deep(.tab-btn) {
    @apply bg-[#055988] text-white;
    &.is-active {
        @apply text-[#055988] bg-white;
    }
}

:deep(.date-time-picker__icon) {
    @apply mb-0.5 h-4 w-4;
}

:deep(.vc-highlight-content-solid) {
    @apply !bg-[#05598829];
}

:deep(.search-input input:focus),
:deep(.dropdown.dropdown--active),
:deep(.date-time-picker.date-time-picker--active),
:deep(.date-time-picker__value:focus-visible),
:deep(.dropdown__wrapper:focus .dropdown),
:deep(.tab-btn.is-active:focus-visible),
:deep(.search-input input.is-focused) {
    outline-color: #055988 !important;
}

:deep(.tabbed-searchbox-hero__title) {
    @apply text-2xl md:leading-[54px] md:text-[54px] font-bold;
}

:deep(.tabbed-searchbox-hero__subtitle) {
    @apply text-base md:text-xl font-light;
}

:deep(.customers-selector-wrapper__tooltip-icon) {
    @apply -mt-0.5 h-5 w-5 !text-[#a2a5bb];
}

:deep(.customers-selector-wrapper__tooltip) {
    background-color: #282c4a !important;
}

:deep(.date-time-picker:hover),
:deep(.search-input__input:hover),
:deep(.search-input input:hover),
:deep(.dropdown--targetable:hover) {
    border-color: #05598880;
}

:deep(.calendar-container .vc-focus:focus-within) {
    box-shadow: 0 0 0 4px #055988 !important;
}
</style>
